import {keepPreviousData, queryOptions, useQuery} from "@tanstack/react-query"
import {DateRange} from "react-day-picker"

import * as apiClient from "@/lib/api"
import {ROLES} from "@/lib/auth/roles"
import {formatDate} from "@/lib/filters/date/date-utils"
import useIsQueryEnabled from "@/lib/hooks/query-enabler"
import {useQueryResetWithRefresh} from "@/lib/hooks/query-refetch-with-reset"
import {OpportunitiesNumberData} from "@/modules/elevation/types"
import {useDate} from "@/stores/date-filter"
import {useServiceType} from "@/stores/service-type";
import {useToken} from "@/stores/token"
import {QUERY_KEY_OPPORTUNITIES_NUMBER} from "@/utils/keys"

function fetchOpportunitiesNumber(date: DateRange, serviceType: number | null): Promise<OpportunitiesNumberData> {
  const to = formatDate(date.to, 'yyyy-MM-dd')
  const from = formatDate(date.from, 'yyyy-MM-dd')
  return apiClient.get(`/firm/elevation/scores/number-of-opportunities?&to=${to}&from=${from}&service_type_id=${serviceType}`)
}
export const opportunitiesNumberQueryOptions = (date: DateRange, serviceType: number | null) => queryOptions({
  queryKey: [QUERY_KEY_OPPORTUNITIES_NUMBER, date, serviceType],
  queryFn: () => fetchOpportunitiesNumber(date, serviceType),
  placeholderData: keepPreviousData
})

export const useOpportunitiesNumber = () => {
  const { date } = useDate()
  const { token } = useToken()
  const { serviceType } = useServiceType()

  const queryOptions = opportunitiesNumberQueryOptions(date, serviceType)
  queryOptions.enabled = useIsQueryEnabled(!!(token?.roles.includes(ROLES.ROLE_ELEVATION) && serviceType))

  const query = useQuery(queryOptions)
  const { resetAndRefetch } = useQueryResetWithRefresh(queryOptions.queryKey, query.refetch)

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    isError: query.isError,
    isRefetchError: query.isRefetchError,
    error: query.error,
    isRefetching: query.isRefetching,
    isSuccess: query.isSuccess,
    status: query.status,
    refetch: resetAndRefetch
  }
}

